import * as formik from "formik";
import * as yup from "yup";

import { Form, Modal } from "react-bootstrap";
import { displayName, formatDateToYYYYMMDD } from "../../../commons/util/HallAppUtil";
import { useEffect, useMemo, useState } from "react";

import AppButton from "../../../commons/buttons/AppButton";
import AppContainer from "../../../commons/containers/AppContainer";
import AppInput from "../../../commons/inputs/AppInput";
import AppLinkButton from "../../../commons/buttons/AppLinkButton";
import AppLoading from "../../../commons/AppLoading";
import AppTextAreaInput from "../../../commons/inputs/AppTextAreaInput";
import CalendarInput from "../../../commons/inputs/CalendarInput";
import CompanyAPI from "../../company/CompanyAPI";
import ConfirmModal from "../../../commons/modal/ConfirmModal";
import CustomContainer from "../../../commons/containers/CustomContainer";
import HallAppCenterContainer from "../../../commons/containers/HallAppCenterContainer";
import HallAppToast from "../../../commons/HallAppToast";
import SchedulerConfigAPI from "../SchedulerConfigAPI";
import Select from "react-select";
import UserService from "../../../auth/UserService";
import { useMask } from "@react-input/mask";

const customStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: 56,
    borderRadius: 12,
    borderColor: "#e7e7ee",
    boxShadow: "none",
    "&:hover": { borderColor: "#c9c9d8" },
  }),
  valueContainer: (p) => ({ ...p, padding: "2px 12px" }),
  input: (p) => ({ ...p, margin: 0, padding: 0 }),
  menu: (provided) => ({ ...provided, zIndex: 10001, borderRadius: 12 }),
};

// ✅ helpers
const isValidHHmm = (v) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(String(v || ""));

const timeToMinutes = (v) => {
  const [hh, mm] = String(v).split(":").map((x) => parseInt(x, 10));
  return hh * 60 + mm;
};

function SchedulerClearancesModal({ closeShow, show, fetchAll }) {
  const companyAPI = useMemo(() => new CompanyAPI(), []);
  const schedulerAPI = useMemo(() => new SchedulerConfigAPI(), []);
  const userAPI = useMemo(() => new UserService(), []);

  const [isLoading, setLoading] = useState(false);

  // ✅ range de datas
  const [dateRange, setDateRange] = useState([new Date(), null]);
  const [startDateISO, setStartDateISO] = useState(formatDateToYYYYMMDD(new Date()));
  const [endDateISO, setEndDateISO] = useState(null);

  const [type, setType] = useState("ALL_DAY"); // ALL_DAY | TIME

  const [optionEmployees, setOptionEmployees] = useState([]);
  const [optionEmployeeSelected, setOptionEmployeeSelected] = useState({});
  const [isAdmin, setAdmin] = useState(false);

  // ✅ modal force
  const [showForceModal, setShowForceModal] = useState(false);
  const [forcePayload, setForcePayload] = useState(null);

  const initialValues = {
    startTime: "",
    endTime: "",
    reason: "",
  };

  const { Formik } = formik;

  // ✅ Schema depende do type (SEM context do Formik)
  const schema = useMemo(() => {
    const base = {
      reason: yup.string().max(255, "Motivo muito longo (máx 255)"),
    };

    if (type !== "TIME") {
      return yup.object().shape({
        ...base,
        startTime: yup.string().nullable(),
        endTime: yup.string().nullable(),
      });
    }

    // type === TIME
    return yup.object().shape({
      ...base,

      // 1) obrigatório
      startTime: yup
        .string()
        .required("Informe o horário de início")
        // 2) formato HH:mm
        .test("fmt-start", "Horário de início inválido. Use HH:mm", function (value) {
          if (!String(value || "").trim()) return true; // required já cobre
          return isValidHHmm(value);
        }),

      endTime: yup
        .string()
        .required("Informe o horário de fim")
        .test("fmt-end", "Horário de fim inválido. Use HH:mm", function (value) {
          if (!String(value || "").trim()) return true; // required já cobre
          return isValidHHmm(value);
        })
        // 3) start < end
        .test("end-after-start", "O horário final deve ser maior que o inicial", function (endTime) {
          const { startTime } = this.parent || {};

          if (!String(startTime || "").trim() || !String(endTime || "").trim()) return true;
          if (!isValidHHmm(startTime) || !isValidHHmm(endTime)) return true;

          return timeToMinutes(endTime) > timeToMinutes(startTime);
        }),
    });
  }, [type]);

  useEffect(() => {
    const user = userAPI.getUserData();
    setAdmin(user.rules?.length !== 0 && user.rules?.includes("COMPANY"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) getAllEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const getAllEmployee = async () => {
    try {
      setLoading(true);
      const response = await companyAPI.getAllEmployee(companyAPI.getCompanyManagment().id);
      const options = response.data.map((d) => ({
        value: d.id,
        label: displayName(d.name) || d.email,
      }));
      setOptionEmployeeSelected(options.find((e) => e.value === userAPI.getUserData().id));
      setOptionEmployees(options);
    } catch (_) {
      setOptionEmployeeSelected([]);
    } finally {
      setLoading(false);
    }
  };

  const changeSelectedEmployee = (employee) => {
    setOptionEmployeeSelected(employee);
  };

  const startTimeMask = useMask({ mask: "__:__", replacement: { _: /\d/ } });
  const endTimeMask = useMask({ mask: "__:__", replacement: { _: /\d/ } });

  const extractErrorCode = (err) => {
    const data = err?.response?.data;
    if (Array.isArray(data) && data.length > 0) return data?.[0]?.code;
    if (data && typeof data === "object") return data?.code;
    return undefined;
  };

  function handleChangeDateRange(start, end) {
    const safeStart = start || formatDateToYYYYMMDD(new Date());
    setStartDateISO(safeStart);
    setEndDateISO(end || null);
  }

  const buildPayload = (values, force = false) => {
    const employeeId = optionEmployeeSelected?.value || userAPI.getUserData().id;
    const end = endDateISO || startDateISO;

    return {
      ...values,
      type,
      companyId: companyAPI.getCompanyManagment().id,
      employeeId,
      startDate: startDateISO,
      endDate: end,
      date: startDateISO,
      ...(force ? { force: true } : {}),
    };
  };

  const doCreate = async (payload, { resetForm } = {}) => {
    await schedulerAPI.closedScheduler(payload);
    HallAppToast.success();
    resetForm?.();
    await fetchAll?.();
  };

  const create = async (values, { resetForm }) => {
    if (!startDateISO) {
      HallAppToast.error("Selecione a data de início.");
      return;
    }

    const payload = buildPayload(values, false);

    try {
      setLoading(true);
      await doCreate(payload, { resetForm });
    } catch (err) {
      const code = extractErrorCode(err);
      if (code === "SCHEDULING_EXISTS") {
        setForcePayload({ payload, resetForm });
        setShowForceModal(true);
        return;
      }
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  };

  const confirmForceCreate = async () => {
    if (!forcePayload?.payload) {
      setShowForceModal(false);
      return;
    }

    try {
      setLoading(true);
      const forced = { ...forcePayload.payload, force: true };
      await doCreate(forced, { resetForm: forcePayload.resetForm });

      setShowForceModal(false);
      setForcePayload(null);
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  };

  const rangeLabel = useMemo(() => {
    const [s, e] = dateRange || [];
    if (!s) return "Selecione o período";
    const sTxt = s.toLocaleDateString("pt-BR");
    const eTxt = e ? e.toLocaleDateString("pt-BR") : null;
    return eTxt ? `${sTxt} → ${eTxt}` : sTxt;
  }, [dateRange]);

  return (
    <>
      <Modal show={show} fullscreen onHide={() => closeShow()}>
        <Modal.Header closeButton className="haClear__header">
          <Modal.Title className="haClear__title">Adicionar folga</Modal.Title>
        </Modal.Header>

        <Modal.Body className="haClear__body">
          <CustomContainer>
            <Formik
              validationSchema={schema}
              validateOnBlur
              validateOnChange
              onSubmit={create}
              initialValues={initialValues}
              enableReinitialize
            >
              {({
                handleSubmit,
                handleChange,
                values,
                touched,
                errors,
                setFieldValue,
                setFieldTouched,
              }) => (
                <AppContainer>
                  <Form noValidate onSubmit={handleSubmit} className="haClear__form">
                    {isAdmin && (
                      <div className="haClear__block">
                        <div className="haClear__label">Funcionário</div>
                        <Select
                          menuPlacement="auto"
                          styles={customStyles}
                          options={optionEmployees}
                          placeholder="Selecione..."
                          value={optionEmployeeSelected}
                          onChange={changeSelectedEmployee}
                          classNamePrefix="react-select"
                        />
                      </div>
                    )}

                    <div className="haClear__block">
                      <div className="haClear__label">Período</div>
                      <div className="haClear__hint">{rangeLabel}</div>

                      <div className="haClear__calendar">
                        <CalendarInput
                          selectionMode="range"
                          setDates={setDateRange}
                          changeDates={handleChangeDateRange}
                          values={dateRange}
                          hideOnRangeSelection
                        />
                      </div>
                    </div>

                    <div className="haClear__block">
                      <div className="haClear__label">Tipo de bloqueio</div>

                      <div className="haClear__chips" role="radiogroup" aria-label="Tipo de bloqueio">
                        <button
                          type="button"
                          className={`haClear__chip ${type === "ALL_DAY" ? "is-active" : ""}`}
                          onClick={() => {
                            setType("ALL_DAY");
                            setFieldValue("startTime", "");
                            setFieldValue("endTime", "");
                            setFieldTouched("startTime", false);
                            setFieldTouched("endTime", false);
                          }}
                          role="radio"
                          aria-checked={type === "ALL_DAY"}
                        >
                          Dia todo
                        </button>

                        <button
                          type="button"
                          className={`haClear__chip ${type === "TIME" ? "is-active" : ""}`}
                          onClick={() => setType("TIME")}
                          role="radio"
                          aria-checked={type === "TIME"}
                        >
                          Por horário
                        </button>
                      </div>
                    </div>

                    {type === "TIME" && (
                      <div className="haClear__grid2">
                        <AppInput
                          errors={errors}
                          touched={touched}
                          mask={startTimeMask}
                          name="startTime"
                          onChange={handleChange}
                          value={values.startTime}
                          label="Início"
                          placeholder="HH:mm"
                        />
                        <AppInput
                          errors={errors}
                          touched={touched}
                          mask={endTimeMask}
                          name="endTime"
                          onChange={handleChange}
                          value={values.endTime}
                          label="Fim"
                          placeholder="HH:mm"
                        />
                      </div>
                    )}

                    <AppTextAreaInput
                      errors={errors}
                      touched={touched}
                      name="reason"
                      onChange={handleChange}
                      value={values.reason}
                      type="text"
                      label="Motivo"
                    />

                    <div className="haClear__footer">
                      {isLoading ? (
                        <HallAppCenterContainer>
                          <AppLoading />
                        </HallAppCenterContainer>
                      ) : (
                        <AppButton type="submit" width="100%" name="adicionar" />
                      )}

                      <div className="mt-2">
                        <AppLinkButton name="fechar" onClick={closeShow} />
                      </div>
                    </div>
                  </Form>
                </AppContainer>
              )}
            </Formik>
          </CustomContainer>
        </Modal.Body>
      </Modal>

      {showForceModal && (
        <ConfirmModal
          textBtnConfirm="sim"
          show={showForceModal}
          title="Atenção"
          isLoading={isLoading}
          confirm={confirmForceCreate}
          closeModal={() => {
            setShowForceModal(false);
            setForcePayload(null);
          }}
        >
          <p>
            Já existe um <strong>agendamento</strong> dentro do <strong>período selecionado</strong>.
          </p>
          <p>
            Ao continuar, este <strong>bloqueio</strong> será criado mesmo assim e pode{" "}
            <strong>impactar agendamentos existentes</strong>.
          </p>
          <p>
            <strong>Importante:</strong> agendamentos existentes <strong>não serão cancelados automaticamente</strong>.
            Se necessário, o cancelamento deve ser feito <strong>manualmente</strong>.
          </p>
          <p>
            <strong>Deseja forçar a criação do bloqueio?</strong>
          </p>
        </ConfirmModal>
      )}
    </>
  );
}

export default SchedulerClearancesModal;
